import { useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { toast } from "sonner"
import { Plus, X, Check, ChevronRight } from "lucide-react"
import {
  addBodyMeasurementsEntry, getBodyMeasurementsHistory, getWeightHistory,
  BODY_FAT_SOURCE_LABELS,
  type BodyMeasurementsRow, type BodyMeasurementsInput, type BodyFatSource,
} from "@/lib/supabase"
import {
  computeWaistToHeightRatio, computeBodyFatPercentUSNavy, computeLeanMassKg,
  isPresentMeasure, WAIST_TO_HEIGHT_HEALTHY_THRESHOLD, type Gender,
} from "@/lib/bodyComposition"
import { toLocalISODate, formatFr, formatDateShortFr } from "@/lib/utils"
import { useAuth } from "@/contexts/AuthContext"
import { SectionTitle, SectionCaption } from "@/components/profile/ui"
import { isMeasurementUpToDate, type TestingSchedule } from "@/hooks/useTestingSchedule"
import {
  HistoryHero, HistoryChart, HistoryTimeline, type HistoryPoint,
} from "@/components/profile/HistoryView"

interface Props {
  gender: Gender
  heightCm: number
  currentWeightKg: number
  /**
   * Calendrier des tests du bloc. Les relevés de mesures suivent le MÊME rythme que les
   * tests de force (programs.testing_sessions) : la section ne recalcule pas d'intervalle
   * de son côté, elle lit l'échéance qu'on lui passe.
   */
  schedule: TestingSchedule
}

// ── Brouillon local du formulaire « Ajouter un relevé » ──
// Même principe que la persistance du chrono repos (SessionPage) : la page peut
// être démontée à tout moment (changement d'onglet du Profil, verrouillage iOS)
// sans que les handlers de fermeture ne se déclenchent. On garde donc la saisie
// en cours dans localStorage, sous une clé propre à l'utilisateur pour ne pas
// fuiter entre deux comptes sur le même appareil. Effacé à la soumission réussie
// comme à l'annulation explicite.
const MEASUREMENTS_DRAFT_PREFIX = "zeyone_measurements_draft_"

interface MeasurementsDraft {
  formValues: Record<string, string>
  bodyFatManual: string
  bodyFatSource: BodyFatSource
}

function draftHasContent(d: MeasurementsDraft): boolean {
  return d.bodyFatManual.trim() !== "" || Object.values(d.formValues).some((v) => v.trim() !== "")
}

function loadMeasurementsDraft(userId: string | null): MeasurementsDraft | null {
  if (!userId) return null
  try {
    const raw = localStorage.getItem(MEASUREMENTS_DRAFT_PREFIX + userId)
    if (!raw) return null
    const d = JSON.parse(raw) as MeasurementsDraft
    return draftHasContent(d) ? d : null
  } catch {
    return null
  }
}

function saveMeasurementsDraft(userId: string | null, draft: MeasurementsDraft): void {
  if (!userId) return
  try {
    if (draftHasContent(draft)) {
      localStorage.setItem(MEASUREMENTS_DRAFT_PREFIX + userId, JSON.stringify(draft))
    } else {
      localStorage.removeItem(MEASUREMENTS_DRAFT_PREFIX + userId)
    }
  } catch {
    // quota / mode privé — on dégrade en mémoire seule
  }
}

function clearMeasurementsDraft(userId: string | null): void {
  if (!userId) return
  try {
    localStorage.removeItem(MEASUREMENTS_DRAFT_PREFIX + userId)
  } catch {
    // ignore
  }
}

// Champs cm bruts stockés tels quels — pas de calcul dérivé.
type NumericField =
  | "waist_cm" | "shoulders_cm" | "chest_cm" | "arm_right_cm" | "arm_left_cm"
  | "forearm_cm" | "thigh_cm" | "calf_cm"

// + les deux grandeurs dérivées, recalculées à chaque relevé de l'historique.
type HistoryFieldKey = NumericField | "body_fat" | "lean_mass"

// Chaque mensuration a sa silhouette, zone mesurée allumée : c'est la partie du corps
// qui doit se reconnaître d'un coup d'œil, pas l'outil de mesure.
const MENSURATION_FIELDS: { field: NumericField; label: string; illustration: string }[] = [
  { field: "shoulders_cm", label: "Épaules", illustration: "/illustrations/mesure-epaules.svg" },
  { field: "chest_cm", label: "Poitrine", illustration: "/illustrations/mesure-poitrine.svg" },
  { field: "arm_right_cm", label: "Bras droit", illustration: "/illustrations/mesure-bras-droit.svg" },
  { field: "arm_left_cm", label: "Bras gauche", illustration: "/illustrations/mesure-bras-gauche.svg" },
  { field: "forearm_cm", label: "Avant-bras", illustration: "/illustrations/mesure-avant-bras.svg" },
  { field: "thigh_cm", label: "Cuisse", illustration: "/illustrations/mesure-cuisse.svg" },
  { field: "calf_cm", label: "Mollet", illustration: "/illustrations/mesure-mollet.svg" },
]

type CmField = "waist_cm" | "neck_cm" | "hips_cm" | NumericField

const FORM_CM_FIELDS: { field: CmField; label: string }[] = [
  { field: "waist_cm", label: "Tour de taille" },
  { field: "neck_cm", label: "Tour de cou" },
  { field: "hips_cm", label: "Tour de hanches" },
  { field: "shoulders_cm", label: "Épaules" },
  { field: "chest_cm", label: "Poitrine" },
  { field: "arm_right_cm", label: "Bras droit" },
  { field: "arm_left_cm", label: "Bras gauche" },
  { field: "forearm_cm", label: "Avant-bras" },
  { field: "thigh_cm", label: "Cuisse" },
  { field: "calf_cm", label: "Mollet" },
]

function effectiveBodyFat(
  row: BodyMeasurementsRow | null | undefined,
  gender: Gender,
  heightCm: number
): { value: number; sourceLabel: string } | null {
  if (!row) return null
  if (isPresentMeasure(row.body_fat_pct_manual)) {
    return { value: row.body_fat_pct_manual, sourceLabel: BODY_FAT_SOURCE_LABELS[row.body_fat_source ?? "coach"] }
  }
  const computed = computeBodyFatPercentUSNavy({
    gender, neckCm: row.neck_cm, waistCm: row.waist_cm, heightCm, hipsCm: row.hips_cm,
  })
  if (computed == null) return null
  return { value: computed, sourceLabel: "estimation ruban" }
}

function missingMeasuresHint(gender: Gender): string {
  return gender === "female" ? "Cou + taille + hanches requis" : "Cou + taille requis"
}

function latestAndPrevious(rows: BodyMeasurementsRow[], field: NumericField) {
  const withValue = rows.filter((r) => r[field] != null)
  const n = withValue.length
  if (n === 0) return { latest: null as number | null, previous: null as number | null }
  const latest = withValue[n - 1][field] as number
  const previous = n >= 2 ? (withValue[n - 2][field] as number) : null
  return { latest, previous }
}

/**
 * Points d'historique d'une mesure, du plus ancien au plus récent.
 *
 * Aucune table nouvelle n'est nécessaire : `body_measurements` porte déjà une ligne par
 * date de relevé, l'historique existe depuis le premier jour. Les deux grandeurs dérivées
 * (masse grasse, masse maigre) sont recalculées relevé par relevé plutôt que lues — elles
 * ne sont stockées nulle part, exactement comme sur la ligne courante de la section.
 *
 * Ordre CHRONOLOGIQUE, à l'inverse de ce que la modale affichait avant : la courbe et
 * l'écart total en dépendent, et `HistoryTimeline` se charge d'inverser pour l'affichage.
 */
function buildHistoryPoints(
  field: HistoryFieldKey,
  rows: BodyMeasurementsRow[],
  gender: Gender,
  heightCm: number,
  weightByDate: Record<string, number>
): HistoryPoint[] {
  const asc = [...rows].sort((a, b) => a.date.localeCompare(b.date))

  if (field === "body_fat") {
    return asc.flatMap((r) => {
      const bf = effectiveBodyFat(r, gender, heightCm)
      // La provenance devient le libellé d'étape : d'un relevé à l'autre elle change
      // (ruban, impédance, DEXA), et comparer deux méthodes n'a pas le même sens que
      // comparer deux mesures d'une même méthode.
      return bf ? [{ id: r.id, date: r.date, value: bf.value, label: bf.sourceLabel }] : []
    })
  }

  if (field === "lean_mass") {
    return asc.flatMap((r) => {
      const bf = effectiveBodyFat(r, gender, heightCm)
      const lean = computeLeanMassKg(weightByDate[r.date], bf?.value ?? null)
      return lean != null ? [{ id: r.id, date: r.date, value: lean }] : []
    })
  }

  return asc.flatMap((r) =>
    r[field] != null ? [{ id: r.id, date: r.date, value: r[field] as number }] : []
  )
}

/** Valeur d'historique dans son unité. Le `~` de la masse grasse rappelle une estimation. */
function formatHistoryValue(field: HistoryFieldKey, value: number): string {
  if (field === "body_fat") return `~${Math.round(value)} %`
  if (field === "lean_mass") return `${formatFr(value)} kg`
  return `${formatFr(value)} cm`
}

/** Ce qu'il manque pour qu'une grandeur dérivée soit calculable, quand elle ne l'est pas. */
function historyEmptyHint(field: HistoryFieldKey, gender: Gender): string {
  if (field === "body_fat") return missingMeasuresHint(gender)
  if (field === "lean_mass") return "Masse grasse requise"
  return "Ajoute un relevé pour commencer à suivre cette mesure."
}

/**
 * Ligne de repère temporel affichée sous l'en-tête : de quand date le dernier relevé, et
 * quand le suivant est attendu.
 *
 * L'échéance vient du calendrier de tests du bloc et s'exprime en SEMAINE DE PROGRAMME,
 * jamais en date. Une version antérieure projetait la date (`début de bloc + semaine × 7`)
 * et affichait des « conseils » déjà passés dès que le rythme réel du client décrochait du
 * rythme théorique — voir l'avertissement en tête de `useTestingSchedule`. Même principe
 * que le radar juste en dessous, qui n'annonce que « à la séance 48 (semaine 12) ».
 *
 * Deux formulations selon que le relevé est à jour au sens de `isMeasurementUpToDate` :
 * tant qu'il l'est on annonce la prochaine échéance ; sinon on pointe celle qui est déjà
 * passée, parce que « conseillé semaine 12 » alors que le relevé de la semaine 5 manque
 * encore désigne la mauvaise échéance.
 */
function measurementsCaption(lastDate: string | null, schedule: TestingSchedule): string {
  const head = lastDate ? `Dernier relevé le ${formatDateShortFr(lastDate)}` : "Aucun relevé pour l'instant"

  if (!isMeasurementUpToDate(lastDate, schedule.lastRmDate)) {
    const due = testMoment(schedule.lastTestWeek, schedule.lastTestSession)
    if (due) return `${head} · relevé attendu depuis la ${due}`
  }
  const next = testMoment(schedule.nextTestWeek, schedule.nextTestSession)
  if (next) return `${head} · prochain relevé conseillé ${next}`

  // Ni échéance passée ni échéance à venir : plus aucun test prévu dans le bloc, ou le
  // programme n'expose pas ses séances de test. On s'arrête à ce qu'on sait.
  return head
}

/**
 * Désigne un moment du bloc : « semaine 12 (séance 48) ». La semaine passe devant parce
 * qu'un relevé de mesures se situe dans une semaine, pas à une séance précise ; le numéro
 * de séance suit, entre parenthèses, pour faire le lien avec le repère du radar — les deux
 * sections pointent le même moment. Renvoie null si ni l'un ni l'autre n'est connu.
 */
function testMoment(week: number | null, session: number | null): string | null {
  if (week != null) return session != null ? `semaine ${week} (séance ${session})` : `semaine ${week}`
  return session != null ? `séance de test ${session}` : null
}

function signedDelta(delta: number, unit: string): string {
  const sign = delta > 0 ? "+" : delta < 0 ? "−" : ""
  return `${sign}${formatFr(Math.abs(delta))} ${unit} vs relevé précédent`
}

// ── Ligne de mesure (même gabarit que la liste 1RM du Profil) ──
function MeasureRow({
  illustration, label, valueDisplay, subDisplay, unavailableHint, isLast, onClick,
}: {
  /** Chemin public d'une illustration mesure-*.svg (voir scripts/generate-measure-illustrations.mjs). */
  illustration: string; label: string
  valueDisplay: string | null; subDisplay?: string | null; unavailableHint?: string | null
  isLast: boolean; onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3.5 px-4 py-3.5 cursor-pointer transition-all hover:bg-white/[0.02] active:scale-[0.99] text-left"
      style={{ borderBottom: isLast ? "none" : "1px solid rgba(30,41,59,0.3)" }}
    >
      {/* Pas de pastille teintée derrière : l'illustration porte déjà sa couleur, son volume
          et son ombre, comme les tuiles Poids/Taille/IMC. Une pastille en plus rapetissait la
          silhouette de 4 px — précisément ce qui manque pour lire la zone à cette taille. */}
      <img src={illustration} alt="" aria-hidden className="w-8 h-8 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-gray-300">{label}</div>
        {subDisplay && <div className="text-[10.5px] text-gray-600 mt-0.5">{subDisplay}</div>}
      </div>
      {valueDisplay ? (
        <span className="text-[14px] font-bold text-white text-right shrink-0 tnum">{valueDisplay}</span>
      ) : (
        <span className="text-gray-600 font-normal text-[11.5px] text-right shrink-0 max-w-[110px]">{unavailableHint}</span>
      )}
      <ChevronRight className="w-4 h-4 text-gray-700 shrink-0" />
    </button>
  )
}

export function MeasurementsSection({ gender, heightCm, currentWeightKg, schedule }: Props) {
  const { user } = useAuth()
  const userId = user?.id ?? null
  const [rows, setRows] = useState<BodyMeasurementsRow[]>([])
  const [weightByDate, setWeightByDate] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formValues, setFormValues] = useState<Record<string, string>>({})
  const [bodyFatManual, setBodyFatManual] = useState("")
  const [bodyFatSource, setBodyFatSource] = useState<BodyFatSource>("impedance")
  const [historyField, setHistoryField] = useState<HistoryFieldKey | null>(null)
  const [historyLabel, setHistoryLabel] = useState("")

  useEffect(() => {
    let cancelled = false
    Promise.all([getBodyMeasurementsHistory(3650), getWeightHistory(3650)])
      .then(([measurements, weights]) => {
        if (cancelled) return
        setRows(measurements)
        const map: Record<string, number> = {}
        for (const w of weights) map[w.date] = w.weight
        setWeightByDate(map)
      })
      .catch((e) => console.error("[MeasurementsSection] fetch error:", e))
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // Sauvegarde continue du brouillon tant que le formulaire est ouvert. Le
  // démontage de la page (onglet, verrouillage écran) ne déclenche aucun
  // handler de fermeture : c'est ce passage en localStorage qui préserve la
  // saisie jusqu'à la prochaine ouverture du formulaire.
  useEffect(() => {
    if (!showForm) return
    saveMeasurementsDraft(userId, { formValues, bodyFatManual, bodyFatSource })
  }, [showForm, formValues, bodyFatManual, bodyFatSource, userId])

  const latest = rows.length > 0 ? rows[rows.length - 1] : null

  const bodyFat = useMemo(() => effectiveBodyFat(latest, gender, heightCm), [latest, gender, heightCm])
  const leanMassKg = computeLeanMassKg(currentWeightKg, bodyFat?.value ?? null)
  const waistRatio = computeWaistToHeightRatio(latest?.waist_cm ?? null, heightCm)

  const openHistory = (field: HistoryFieldKey, label: string) => {
    setHistoryField(field)
    setHistoryLabel(label)
  }

  // Pré-remplit avec le relevé du jour s'il existe déjà : l'upsert onConflict(user_id,date)
  // ne fait qu'un merge partiel (les colonnes non envoyées gardent leur valeur précédente),
  // donc si on ouvrait le formulaire vide, une resaisie partielle plus tard dans la journée
  // laisserait silencieusement des mesures d'un essai précédent (ex. un tour de cou de test)
  // continuer à alimenter les calculs sans que le client les voie ni les ait revalidées.
  const openForm = () => {
    const today = toLocalISODate(new Date())
    const todayRow = rows.find((r) => r.date === today)
    const prefill: Record<string, string> = {}
    if (todayRow) {
      for (const { field } of FORM_CM_FIELDS) {
        const v = todayRow[field]
        if (v != null) prefill[field] = String(v)
      }
    }
    // Un brouillon non soumis reprend la main sur le pré-remplissage : ce sont
    // les valeurs que l'utilisateur était en train de saisir avant de quitter
    // la page. Sinon, on repart du relevé du jour (ou d'un formulaire vide).
    const draft = loadMeasurementsDraft(userId)
    if (draft) {
      setFormValues(draft.formValues)
      setBodyFatManual(draft.bodyFatManual)
      setBodyFatSource(draft.bodyFatSource)
    } else {
      setFormValues(prefill)
      setBodyFatManual(todayRow?.body_fat_pct_manual != null ? String(todayRow.body_fat_pct_manual) : "")
      if (todayRow?.body_fat_source) setBodyFatSource(todayRow.body_fat_source)
    }
    setShowForm(true)
  }

  // Fermeture explicite (Annuler / croix / clic hors modale) : le brouillon est
  // abandonné, contrairement à un simple démontage de la page.
  const closeForm = () => {
    clearMeasurementsDraft(userId)
    setShowForm(false)
  }

  const handleSave = async () => {
    if (saving) return
    // Le formulaire représente l'état COMPLET du relevé du jour (pré-rempli par openForm
    // avec ce qui existe déjà) : chaque champ doit donc être envoyé explicitement, y compris
    // à `null` quand il est vide. Omettre une clé vide du payload ne suffit pas — l'upsert
    // onConflict(user_id,date) ne touche que les colonnes présentes dans la requête, donc une
    // clé omise laisse silencieusement l'ancienne valeur en base (bug reproduit : vider le
    // tour de cou puis enregistrer ne l'effaçait pas, il restait attaché à la ligne du jour).
    const input: BodyMeasurementsInput = {}
    for (const { field } of FORM_CM_FIELDS) {
      const raw = formValues[field]?.trim()
      if (!raw) {
        ;(input as Record<CmField, number | null>)[field] = null
        continue
      }
      const n = parseFloat(raw.replace(",", "."))
      if (isNaN(n) || n <= 0 || n > 300) {
        toast.error("Mesure invalide", { description: "Vérifie les valeurs saisies (en cm)." })
        return
      }
      ;(input as Record<CmField, number | null>)[field] = n
    }
    const rawBf = bodyFatManual.trim()
    if (!rawBf) {
      input.body_fat_pct_manual = null
      input.body_fat_source = null
    } else {
      const n = parseFloat(rawBf.replace(",", "."))
      if (isNaN(n) || n <= 0 || n > 70) {
        toast.error("Masse grasse invalide")
        return
      }
      input.body_fat_pct_manual = n
      input.body_fat_source = bodyFatSource
    }

    const hasAnyValue = Object.values(input).some((v) => v != null)
    if (!hasAnyValue) {
      toast.error("Renseigne au moins une mesure")
      return
    }

    setSaving(true)
    try {
      const today = toLocalISODate(new Date())
      const saved = await addBodyMeasurementsEntry(today, input)
      setRows((prev) => {
        const filtered = prev.filter((r) => r.date !== today)
        return [...filtered, saved].sort((a, b) => a.date.localeCompare(b.date))
      })
      clearMeasurementsDraft(userId)
      toast.success("Relevé enregistré")
      setShowForm(false)
    } catch (err) {
      toast.error("Erreur", { description: err instanceof Error ? err.message : "Sauvegarde échouée" })
    } finally {
      setSaving(false)
    }
  }

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }} className="px-4 mt-6 relative z-10">
      <SectionTitle
        label="Mes mesures"
        right={
          <button onClick={openForm} className="flex items-center gap-1 text-[11.5px] font-semibold text-cyan-400 cursor-pointer hover:opacity-80">
            <Plus className="w-3.5 h-3.5" /> Ajouter un relevé
          </button>
        }
      />

      {!loading && !schedule.loading && (
        <SectionCaption>{measurementsCaption(latest?.date ?? null, schedule)}</SectionCaption>
      )}

      {loading ? (
        <p className="text-[12px] text-gray-500 text-center py-6">Chargement…</p>
      ) : (
        <>
          <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider ml-1 mb-2">Composition</p>
          <div className="bg-gray-900/70 border border-gray-800/60 rounded-2xl overflow-hidden mb-3">
            <MeasureRow
              illustration="/illustrations/mesure-tour-de-taille.svg" label="Tour de taille"
              valueDisplay={latest?.waist_cm != null ? `${formatFr(latest.waist_cm)} cm` : null}
              subDisplay={waistRatio != null ? `Ratio ${formatFr(waistRatio, 2)} · seuil santé < ${formatFr(WAIST_TO_HEIGHT_HEALTHY_THRESHOLD, 2)}` : null}
              unavailableHint="Aucun relevé"
              isLast={false}
              onClick={() => openHistory("waist_cm", "Tour de taille")}
            />
            <MeasureRow
              illustration="/illustrations/mesure-masse-grasse.svg" label="Masse grasse"
              valueDisplay={bodyFat ? `~${Math.round(bodyFat.value)} %` : null}
              subDisplay={bodyFat ? bodyFat.sourceLabel : null}
              unavailableHint={missingMeasuresHint(gender)}
              isLast={false}
              onClick={() => openHistory("body_fat", "Masse grasse")}
            />
            <MeasureRow
              illustration="/illustrations/mesure-masse-maigre.svg" label="Masse maigre"
              valueDisplay={leanMassKg != null ? `${formatFr(leanMassKg)} kg` : null}
              unavailableHint="Masse grasse requise"
              isLast
              onClick={() => openHistory("lean_mass", "Masse maigre")}
            />
          </div>

          <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider ml-1 mb-2">Mensurations</p>
          <div className="bg-gray-900/70 border border-gray-800/60 rounded-2xl overflow-hidden">
            {MENSURATION_FIELDS.map((f, i, arr) => {
              const { latest: v, previous } = latestAndPrevious(rows, f.field)
              const delta = v != null && previous != null ? v - previous : null
              return (
                <MeasureRow
                  key={f.field}
                  illustration={f.illustration} label={f.label}
                  valueDisplay={v != null ? `${formatFr(v)} cm` : null}
                  subDisplay={delta != null && delta !== 0 ? signedDelta(delta, "cm") : null}
                  unavailableHint="Aucun relevé"
                  isLast={i === arr.length - 1}
                  onClick={() => openHistory(f.field, f.label)}
                />
              )
            })}
          </div>
        </>
      )}

      {/* MODAL — Ajouter un relevé */}
      <AnimatePresence>
        {showForm && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-[4px]"
              onClick={() => !saving && closeForm()} />
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
              className="fixed z-[100] left-4 right-4 top-1/2 -translate-y-1/2 max-w-[440px] mx-auto p-6 rounded-2xl max-h-[85vh] overflow-y-auto"
              style={{ background: "linear-gradient(180deg,#0f1829,#0a1020)", border: "1px solid rgba(30,41,59,0.55)" }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[16px] font-bold text-white">{rows.some((r) => r.date === toLocalISODate(new Date())) ? "Relevé du jour" : "Nouveau relevé"}</h3>
                <button onClick={() => !saving && closeForm()} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-white/[0.04]">
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              <p className="text-[12px] text-gray-500 mb-4">
                {rows.some((r) => r.date === toLocalISODate(new Date()))
                  ? "Un relevé existe déjà aujourd'hui — les champs déjà remplis sont pré-affichés, modifie ou complète ce qu'il faut."
                  : "Tous les champs sont optionnels — renseigne ce que tu as mesuré aujourd'hui."}
              </p>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {FORM_CM_FIELDS.map(({ field, label }) => (
                  <label key={field} className="flex flex-col gap-1">
                    <span className="text-[11px] text-gray-500">{label}</span>
                    <div className="flex items-center gap-2 px-3 h-[42px] rounded-lg" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)" }}>
                      <input
                        type="number" step="0.1" inputMode="decimal" placeholder="—"
                        value={formValues[field] ?? ""}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, [field]: e.target.value }))}
                        className="flex-1 min-w-0 bg-transparent outline-none text-[14px] text-white placeholder:text-gray-700"
                        style={{ caretColor: "#06b6d4" }}
                      />
                      <span className="text-[11px] text-gray-500 shrink-0">cm</span>
                    </div>
                  </label>
                ))}
              </div>

              <div className="mb-4 pt-3" style={{ borderTop: "1px solid rgba(30,41,59,0.4)" }}>
                <p className="text-[11px] text-gray-500 mb-2">Masse grasse — saisie manuelle (facultatif, prime sur le calcul automatique)</p>
                <div className="flex items-center gap-3 px-4 h-[48px] rounded-xl mb-2" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)" }}>
                  <input
                    type="number" step="0.1" inputMode="decimal" placeholder="Ex: 15"
                    value={bodyFatManual} onChange={(e) => setBodyFatManual(e.target.value)}
                    className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-gray-600"
                    style={{ caretColor: "#f59e0b" }}
                  />
                  <span className="text-[13px] text-gray-500">%</span>
                </div>
                <select
                  value={bodyFatSource} onChange={(e) => setBodyFatSource(e.target.value as BodyFatSource)}
                  className="w-full px-4 h-[44px] rounded-xl text-[13px] text-white outline-none cursor-pointer"
                  style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)" }}
                >
                  {(Object.entries(BODY_FAT_SOURCE_LABELS) as [BodyFatSource, string][]).map(([value, label]) => (
                    <option key={value} value={value} style={{ background: "#0f1829" }}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button onClick={closeForm} disabled={saving}
                  className="py-3 rounded-xl text-[14px] font-semibold text-gray-400 cursor-pointer disabled:opacity-50"
                  style={{ border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}>
                  Annuler
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="py-3 rounded-xl text-[14px] font-bold text-white cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2"
                  style={{ background: "linear-gradient(135deg,#0369a1,#06b6d4)" }}>
                  {saving ? <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" /> : <Check className="w-4 h-4" />}
                  {saving ? "…" : "Enregistrer"}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* MODAL — Historique d'une mesure */}
      <AnimatePresence>
        {historyField && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-[4px]"
              onClick={() => setHistoryField(null)} />
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
              className="fixed z-[100] left-4 right-4 top-1/2 -translate-y-1/2 max-w-[420px] mx-auto p-6 rounded-2xl max-h-[75vh] overflow-y-auto"
              style={{ background: "linear-gradient(180deg,#0f1829,#0a1020)", border: "1px solid rgba(30,41,59,0.55)" }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[16px] font-bold text-white">{historyLabel} · historique</h3>
                <button onClick={() => setHistoryField(null)} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-white/[0.04]">
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              {(() => {
                const points = buildHistoryPoints(historyField, rows, gender, heightCm, weightByDate)
                const formatValue = (v: number) => formatHistoryValue(historyField, v)

                if (points.length === 0) {
                  return (
                    <div className="text-center py-6">
                      <p className="text-[13px] text-gray-400">Aucun relevé pour cette mesure</p>
                      <p className="text-[11.5px] text-gray-600 mt-1.5">{historyEmptyHint(historyField, gender)}</p>
                    </div>
                  )
                }

                return (
                  <>
                    {/* Même lecture que l'historique d'un record : la valeur courante en
                        grand, puis la courbe, puis la liste datée. Les écarts restent en
                        gris — sur un tour de taille, le sens d'une hausse dépend de
                        l'objectif du client, l'app ne le tranche pas à sa place. */}
                    <HistoryHero
                      points={points}
                      formatValue={formatValue}
                      deltaTone="neutral"
                      caption={
                        points.length === 1
                          ? "Un seul relevé pour l'instant — la courbe apparaîtra dès le suivant."
                          : undefined
                      }
                    />

                    {points.length >= 2 && (
                      <div
                        className="mt-4 rounded-[18px] px-1.5 pt-3 pb-1.5"
                        style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)" }}
                      >
                        <HistoryChart points={points} ariaLabel={`Évolution — ${historyLabel}`} />
                      </div>
                    )}

                    <div
                      className="mt-4 rounded-[18px] overflow-hidden"
                      style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)" }}
                    >
                      <HistoryTimeline points={points} formatValue={formatValue} deltaTone="neutral" />
                    </div>
                  </>
                )
              })()}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
